import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1';

// Configurar interceptor para incluir token de autenticação
axios.interceptors.request.use((config) => {
  let token = localStorage.getItem('access_token');
  
  // Se não houver token, usar token temporário para desenvolvimento
  if (!token) {
    token = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyLCJlbWFpbCI6ImhlbGRlcnNhdG9AZ21haWwuY29tIn0.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c';
    localStorage.setItem('access_token', token);
  }
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

class IntegrationsService {
  constructor() {
    this._config = null;
  }

  /**
   * Busca configurações do backend para integrações
   */
  async getConfig() {
    if (!this._config) {
      try {
        const response = await axios.get(`${API_BASE_URL}/integrations/config`);
        this._config = response.data;
      } catch (error) {
        console.warn('Erro ao buscar configurações, usando fallback:', error);
        // Fallback para configurações locais
        this._config = {
          oauth_allowed_origins: [
            window.location.origin,
            'https://fabricadeagentes.ai',
            'https://api.fabricadeagentes.ai'
          ]
        };
      }
    }
    return this._config;
  }

  /**
   * Lista ferramentas disponíveis para integração
   */
  async getAvailableTools() {
    try {
      // Cache de 60s para reduzir chamadas repetidas por subcomponentes
      if (!this._cache) this._cache = { tools: null };
      const cached = this._cache.tools;
      if (cached && cached.expiresAt > Date.now()) return cached.data;

      const response = await axios.get(`${API_BASE_URL}/integrations/available-tools`);
      this._cache.tools = { data: response.data, expiresAt: Date.now() + 60000 };
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar ferramentas disponíveis:', error);
      throw new Error('Falha ao carregar ferramentas disponíveis');
    }
  }

  /**
   * Lista integrações de um agente específico
   */
  async getAgentIntegrations(agentId) {
    try {
      const response = await axios.get(`${API_BASE_URL}/integrations/agent/${agentId}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar integrações do agente:', error);
      throw new Error('Falha ao carregar integrações do agente');
    }
  }

  /**
   * Cria uma nova integração para um agente
   */
  async createIntegration(integrationData) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/integrations/agent/${integrationData.agent_id}`,
        integrationData
      );
      return response.data;
    } catch (error) {
      console.error('Erro ao criar integração:', error);
      throw new Error(error.response?.data?.detail || 'Falha ao criar integração');
    }
  }

  /**
   * Atualiza uma integração existente
   */
  async updateIntegration(integrationId, integrationData) {
    try {
      // Vamos criar uma nova integração em vez de tentar atualizar a existente
      // Isso contorna o problema do erro "SyncSelectRequestBuilder object has no attribute 'join'"
      
      // Primeiro, excluímos a integração existente
      await this.deleteIntegration(integrationId);
      
      // Em seguida, criamos uma nova integração com os mesmos dados
      const response = await this.createIntegration(integrationData);
      
      return response;
    } catch (error) {
      console.error('Erro ao atualizar integração:', error);
      throw new Error(error.response?.data?.detail || 'Falha ao atualizar integração');
    }
  }

  /**
   * Remove uma integração
   */
  async deleteIntegration(integrationId) {
    try {
      const response = await axios.delete(`${API_BASE_URL}/integrations/${integrationId}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao deletar integração:', error);
      throw new Error(error.response?.data?.detail || 'Falha ao deletar integração');
    }
  }

  // =============================================================================
  // MÉTODOS ESPECÍFICOS PARA PIPEDRIVE
  // =============================================================================

  /**
   * Valida token do Pipedrive
   */
  async validatePipedriveToken(token) {
    try {
      const formData = new FormData();
      formData.append('api_token', token);

      const response = await axios.post(
        `${API_BASE_URL}/integrations/pipedrive/validate`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error('Erro ao validar token do Pipedrive:', error);
      return { valid: false, error: error.message };
    }
  }

  /**
   * Busca pipelines do Pipedrive
   */
  async getPipedrivesPipelines(token) {
    try {
      const formData = new FormData();
      formData.append('api_token', token);

      const response = await axios.post(
        `${API_BASE_URL}/integrations/pipedrive/pipelines`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar pipelines do Pipedrive:', error);
      throw new Error('Falha ao buscar pipelines');
    }
  }

  /**
   * Busca estágios de um pipeline específico
   */
  async getPipelineStages(token, pipelineId) {
    try {
      const formData = new FormData();
      formData.append('api_token', token);
      formData.append('pipeline_id', pipelineId);

      const response = await axios.post(
        `${API_BASE_URL}/integrations/pipedrive/stages`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar estágios do pipeline:', error);
      throw new Error('Falha ao buscar estágios do pipeline');
    }
  }

  // =============================================================================
  // MÉTODOS ESPECÍFICOS PARA GOOGLE CALENDAR
  // =============================================================================

  /**
   * Gera URL de autorização para Google Calendar
   */
  async generateGoogleCalendarToken(agentId, userEmail, calendarId = 'primary', timezone = 'America/Sao_Paulo') {
    try {
      const formData = new FormData();
      formData.append('agent_id', agentId);
      formData.append('user_email', userEmail);
      formData.append('calendar_id', calendarId);
      formData.append('timezone', timezone);

      const response = await axios.post(
        `${API_BASE_URL}/integrations/google-calendar/generate-token`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Erro ao gerar token do Google Calendar:', error);
      throw new Error(error.response?.data?.detail || 'Falha ao gerar token');
    }
  }

  /**
   * Completa a autorização do Google Calendar
   */
  async completeGoogleCalendarAuth(authorizationCode, state, tempConfigId) {
    try {
      const formData = new FormData();
      formData.append('authorization_code', authorizationCode);
      formData.append('state', state);
      formData.append('temp_config_id', tempConfigId);

      const response = await axios.post(
        `${API_BASE_URL}/integrations/google-calendar/complete-auth`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Erro ao completar autorização:', error);
      throw new Error(error.response?.data?.detail || 'Falha ao completar autorização');
    }
  }

  /**
   * Upload de configuração do Google Calendar (método manual)
   */
  async uploadGoogleCalendarConfig(formData) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/integrations/google-calendar/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Erro ao fazer upload do Google Calendar:', error);
      throw new Error(error.response?.data?.detail || 'Falha no upload da configuração');
    }
  }

  /**
   * Testa integração do Google Calendar
   */
  async testGoogleCalendarIntegration(agentId) {
    try {
      const formData = new FormData();
      formData.append('agent_id', agentId);

      const response = await axios.post(
        `${API_BASE_URL}/integrations/google-calendar/test`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error('Erro ao testar Google Calendar:', error);
      throw new Error('Falha ao testar integração');
    }
  }

  // =============================================================================
  // MÉTODOS PARA OAUTH AUTOMÁTICO (FUTURO)
  // =============================================================================

  /**
   * Inicia processo OAuth para Google Calendar
   * @deprecated Será implementado na próxima versão
   */
  async startGoogleOAuth(agentId, formData) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/integrations/google-calendar/auth-start`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Erro ao iniciar OAuth do Google:', error);
      throw new Error(error.response?.data?.detail || 'Falha ao iniciar OAuth');
    }
  }

  /**
   * Verifica callback do OAuth
   * @deprecated Será implementado na próxima versão
   */
  async checkOAuthCallback(agentId, code, state) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/integrations/google-calendar/oauth-callback`,
        {
          params: { agent_id: agentId, code, state },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Erro no callback OAuth:', error);
      throw new Error('Falha no callback OAuth');
    }
  }

  // =============================================================================
  // MÉTODOS UTILITÁRIOS
  // =============================================================================

  /**
   * Valida arquivo client_secret.json localmente
   */
  async validateClientSecret(file) {
    try {
      const text = await file.text();
      const json = JSON.parse(text);

      // Verificar estrutura básica
      if (!json.installed) {
        throw new Error('client_secret.json deve ter estrutura "installed"');
      }

      const required = ['client_id', 'client_secret', 'auth_uri', 'token_uri'];
      for (const field of required) {
        if (!json.installed[field]) {
          throw new Error(`Campo obrigatório ausente: ${field}`);
        }
      }

      return true;
    } catch (error) {
      console.error('Erro na validação do client_secret:', error);
      return false;
    }
  }

  /**
   * Validação básica do arquivo token.pickle
   */
  async validateTokenPickle(file) {
    try {
      // Verificar se é um arquivo binário (pickle)
      const buffer = await file.arrayBuffer();
      const bytes = new Uint8Array(buffer);

      // Arquivos pickle começam com bytes específicos
      // Verificação simplificada: deve ter mais de 100 bytes
      return bytes.length > 100;
    } catch (error) {
      console.error('Erro na validação do token.pickle:', error);
      return false;
    }
  }

  /**
   * Obtém configurações específicas de uma integração
   */
  async getIntegrationConfig(agentId, integrationType) {
    try {
      const integrations = await this.getAgentIntegrations(agentId);
      const integration = integrations.find(
        int => int.integration_type === integrationType && int.is_active
      );
      return integration ? integration.config : null;
    } catch (error) {
      console.error('Erro ao buscar configuração da integração:', error);
      return null;
    }
  }

  /**
   * Verifica se uma integração está configurada e ativa
   */
  async isIntegrationActive(agentId, integrationType) {
    try {
      const integrations = await this.getAgentIntegrations(agentId);
      return integrations.some(
        int => int.integration_type === integrationType && int.is_active
      );
    } catch (error) {
      console.error('Erro ao verificar status da integração:', error);
      return false;
    }
  }

  /**
   * Ativa ou desativa uma integração
   */
  async toggleIntegration(integrationId, isActive) {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/integrations/${integrationId}`,
        { is_active: isActive }
      );
      return response.data;
    } catch (error) {
      console.error('Erro ao alterar status da integração:', error);
      throw new Error('Falha ao alterar status da integração');
    }
  }

  /**
   * Regenera token do Google Calendar
   * @deprecated Será implementado na próxima versão
   */
  async regenerateGoogleToken(agentId) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/integrations/google-calendar/regenerate-token`,
        { agent_id: agentId }
      );
      return response.data;
    } catch (error) {
      console.error('Erro ao regenerar token:', error);
      throw new Error('Falha ao regenerar token');
    }
  }
}

// Exportar instância singleton
export const integrationsService = new IntegrationsService();
export default integrationsService; 